"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/v2/Button";
import { Pill } from "@/components/v2/Pill";
import { formatArea, formatPriceFull } from "@/lib/v2/format";
import { columnControl, matchesRange, PriceListColumn } from "./filters";

export type PriceListUnit = {
  id: string;
  status: string;
  unit: string;
  area: number;
  price: number | null;
  pricePerSf: number | null;
  plan: string;
  type: string;
  typeFeature?: string | null;
};

type SortOrder = "asc" | "desc";
type Filters = Partial<Record<PriceListColumn, string>>;

const INITIAL_ROWS = 40;
const OPEN_PRICES_WHILE_LOCKED = 3;
const ROW_HEIGHT = 64;
const HEADER_HEIGHT = 48;
const COLUMNS: Array<{ key: PriceListColumn; label: string; className?: string }> = [
  { key: "status", label: "Status" },
  { key: "unit", label: "Unit" },
  { key: "type", label: "Type" },
  { key: "plan", label: "Plan" },
  { key: "area", label: "Area" },
  { key: "price", label: "Price" },
  { key: "pricePerSf", label: "PSF" },
];

const inputClass =
  "h-10 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none transition placeholder:text-ink-faint focus:border-brand-500 focus:ring-2 focus:ring-brand-100";

function statusTone(status: string): "avail" | "soldout" | "neutral" {
  const normalized = status.trim().toLowerCase();
  if (normalized === "available") return "avail";
  if (normalized === "sold") return "soldout";
  return "neutral";
}

function unitSortValue(value: string): [number, number] | null {
  const match = value.match(/#?(\d+)-(\d+)/);
  return match ? [Number(match[1]), Number(match[2])] : null;
}

function compareValues(
  first: PriceListUnit[PriceListColumn],
  second: PriceListUnit[PriceListColumn],
): number {
  if (typeof first === "string" && typeof second === "string") {
    const firstUnit = unitSortValue(first);
    const secondUnit = unitSortValue(second);
    if (firstUnit && secondUnit) {
      return firstUnit[0] - secondUnit[0] || firstUnit[1] - secondUnit[1];
    }
    return first.localeCompare(second, undefined, { numeric: true });
  }
  if (typeof first === "number" && typeof second === "number") return first - second;
  if (first == null && second == null) return 0;
  if (first == null) return 1;
  if (second == null) return -1;
  return String(first).localeCompare(String(second), undefined, { numeric: true });
}

function LockedValue({ psf = false }: { psf?: boolean }) {
  return (
    <>
      <span className="sr-only">Locked</span>
      <span aria-hidden="true" className="select-none whitespace-nowrap blur-[5px]">
        {psf ? "S$2,XXX psf" : "S$2,1XX,XXX"}
      </span>
    </>
  );
}

function isPricedAvailable(unit: PriceListUnit) {
  const status = unit.status.trim().toLowerCase();
  return unit.price != null && unit.price > 0 && status !== "sold" && status !== "reserved";
}

function displayPrice(value: number | null, status: string, psf = false) {
  if (value != null && value > 0) {
    return psf ? `${formatPriceFull(value)} psf` : formatPriceFull(value);
  }
  return status.trim().toLowerCase() === "sold" ? "Sold" : "On request";
}

export function PriceListTable({
  units,
  locked,
  propertyName,
  selectedPlan,
  onSelectedPlanChange,
  onUnlock,
  floorPlanImage,
  loadError,
}: {
  units: PriceListUnit[];
  locked: boolean;
  propertyName: string;
  selectedPlan: string;
  onSelectedPlanChange: (plan: string) => void;
  onUnlock: () => void;
  floorPlanImage: (plan: string) => string | undefined;
  loadError?: boolean;
}) {
  const [sortKey, setSortKey] = useState<PriceListColumn>("status");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [filters, setFilters] = useState<Filters>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_ROWS);
  const [lightboxPlan, setLightboxPlan] = useState<string | null>(null);

  useEffect(() => {
    setFilters((current) => ({ ...current, plan: selectedPlan }));
    setVisibleCount(INITIAL_ROWS);
    if (selectedPlan) setAdvancedOpen(true);
  }, [selectedPlan]);

  const updateFilter = useCallback(
    (key: PriceListColumn, value: string) => {
      setFilters((current) => ({ ...current, [key]: value }));
      setVisibleCount(INITIAL_ROWS);
      if (key === "plan") onSelectedPlanChange(value);
    },
    [onSelectedPlanChange],
  );

  const typeOptions = useMemo(
    () => Array.from(new Set(units.map((unit) => unit.type).filter(Boolean))).sort(),
    [units],
  );
  const planOptions = useMemo(
    () => Array.from(new Set(units.map((unit) => unit.plan).filter(Boolean))).sort(),
    [units],
  );

  const filteredUnits = useMemo(() => {
    return units
      .filter((unit) =>
        Object.entries(filters).every(([rawKey, expression]) => {
          if (!expression) return true;
          const key = rawKey as PriceListColumn;
          const value = unit[key];
          if (columnControl(key) === "range") {
            const numeric = Number(value);
            return Number.isFinite(numeric) && numeric > 0 && matchesRange(expression, numeric);
          }
          if (key === "status" || key === "type" || key === "plan") {
            return String(value ?? "").toLowerCase() === expression.toLowerCase();
          }
          return String(value ?? "").toLowerCase().includes(expression.toLowerCase());
        }),
      )
      .sort((first, second) => {
        const result = compareValues(first[sortKey], second[sortKey]);
        return sortOrder === "asc" ? result : -result;
      });
  }, [filters, sortKey, sortOrder, units]);

  const visibleUnits = filteredUnits.slice(0, visibleCount);

  // While locked, the first three priced units stay readable as a taste of the
  // list; everything after them is blurred behind the gate.
  const openIds = new Set<string>();
  if (locked) {
    for (const unit of visibleUnits) {
      if (openIds.size >= OPEN_PRICES_WHILE_LOCKED) break;
      if (isPricedAvailable(unit)) openIds.add(unit.id);
    }
  }
  const pricedCount = filteredUnits.filter(isPricedAvailable).length;
  const lastOpenIndex = visibleUnits.reduce((last, unit, index) => (openIds.has(unit.id) ? index : last), -1);
  const gateTop = HEADER_HEIGHT + Math.min(visibleUnits.length, lastOpenIndex + 1) * ROW_HEIGHT;

  const sort = (key: PriceListColumn) => {
    if (sortKey === key) setSortOrder((order) => (order === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  return (
    <section id="table" aria-labelledby="price-list-table-title" className="scroll-mt-24">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 id="price-list-table-title" className="font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
            Units still available
          </h2>
          <p className="mt-1 text-sm text-ink-muted">Filter and sort the developer&apos;s unit list.</p>
        </div>
        <p className="text-sm tabular-nums text-ink-faint">{filteredUnits.length} units shown</p>
      </div>

      {locked && units.length > 0 && pricedCount > 0 && (
        <div className="sticky top-20 z-40 mt-6 flex flex-col gap-3 rounded-2xl border border-brand-100 bg-brand-50/95 px-4 py-3 shadow-card backdrop-blur sm:flex-row sm:items-center sm:justify-between md:px-5">
          <p className="text-sm text-ink">
            <span className="font-semibold">{openIds.size} of {pricedCount} prices shown.</span> Unlock the rest with your name and mobile.
          </p>
          <Button onClick={onUnlock} size="sm" className="shrink-0">Show every price</Button>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-line bg-white p-4 md:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <FilterSelect label="Type" value={filters.type ?? ""} onChange={(value) => updateFilter("type", value)}>
            <option value="">All types</option>
            {typeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
          </FilterSelect>
          <FilterSelect label="Status" value={filters.status ?? ""} onChange={(value) => updateFilter("status", value)}>
            <option value="">All</option>
            <option value="Available">Available</option>
            <option value="Sold">Sold</option>
            <option value="Reserved">Reserved</option>
          </FilterSelect>
          <FilterInput label="Unit" value={filters.unit ?? ""} placeholder="e.g. 03-05" onChange={(value) => updateFilter("unit", value)} />
          <FilterInput label="Price range" value={filters.price ?? ""} placeholder="e.g. < 1.5M, 1.5M-2M" onChange={(value) => updateFilter("price", value)} />
          <FilterInput label="PSF range" value={filters.pricePerSf ?? ""} placeholder="e.g. < 3000" onChange={(value) => updateFilter("pricePerSf", value)} />
        </div>

        <div className="mt-3">
          <Button variant="ghost" size="sm" onClick={() => setAdvancedOpen((open) => !open)} className="-ml-3">
            <SlidersHorizontal aria-hidden="true" className="size-4" />
            Advanced: plan code
            <ChevronDown aria-hidden="true" className={`size-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
          </Button>
          {advancedOpen && (
            <div className="mt-2 max-w-xs">
              <FilterSelect label="Plan code" value={filters.plan ?? ""} onChange={(value) => updateFilter("plan", value)}>
                <option value="">All plans</option>
                {planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
              </FilterSelect>
            </div>
          )}
        </div>
      </div>

      <div className="relative mt-4 overflow-hidden rounded-2xl border border-line bg-white">
        <div className="relative max-h-[70vh] overflow-auto">
          <div className="relative min-w-[940px]">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="sticky top-0 z-30 bg-surface shadow-[0_1px_0_#E3E8F0]">
              <tr>
                {COLUMNS.map(({ key, label }) => (
                  <th key={key} scope="col" aria-sort={sortKey === key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"} className="whitespace-nowrap px-4 py-3 font-semibold text-ink">
                    <button type="button" onClick={() => sort(key)} className="inline-flex items-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                      {label}
                      {sortKey === key ? (sortOrder === "asc" ? <ChevronUp aria-hidden="true" className="size-3.5" /> : <ChevronDown aria-hidden="true" className="size-3.5" />) : <ChevronUp aria-hidden="true" className="size-3.5 opacity-25" />}
                    </button>
                  </th>
                ))}
                <th scope="col" className="whitespace-nowrap px-4 py-3 text-right font-semibold text-ink">View floor plan</th>
              </tr>
            </thead>
            <tbody>
              {visibleUnits.map((unit) => {
                const sold = unit.status.trim().toLowerCase() === "sold";
                const hidden = locked && !openIds.has(unit.id);
                return (
                  <tr key={unit.id} className={`border-b border-line/80 last:border-b-0 ${sold ? "bg-surface/60 text-ink-faint" : "text-ink hover:bg-brand-50/50"}`}>
                    <td className="whitespace-nowrap px-4 py-3.5"><Pill tone={statusTone(unit.status)}>{unit.status}</Pill></td>
                    <td className="whitespace-nowrap px-4 py-3.5 font-semibold tabular-nums">{unit.unit}</td>
                    <td className="px-4 py-3.5">
                      <span className="font-medium">{unit.type}</span>
                      {unit.typeFeature && <span className="mt-0.5 block text-xs text-ink-muted">{unit.typeFeature}</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-xs text-ink-faint">{unit.plan || "Not listed"}</td>
                    <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">{formatArea(unit.area)}</td>
                    <td className={`whitespace-nowrap px-4 py-3.5 font-semibold tabular-nums ${!hidden && unit.price && unit.price > 0 ? "text-gold-600" : ""}`}>
                      {hidden ? <LockedValue /> : displayPrice(unit.price, unit.status)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 tabular-nums">
                      {hidden ? <LockedValue psf /> : displayPrice(unit.pricePerSf, unit.status, true)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3.5 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setLightboxPlan(unit.plan)} className="px-3">View floor plan</Button>
                    </td>
                  </tr>
                );
              })}
              {!visibleUnits.length && (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <p className="font-display text-lg font-semibold text-ink">{loadError ? "The price list could not be loaded" : units.length ? "No units match these filters" : "Price list not yet released"}</p>
                    <p className="mt-1 text-sm text-ink-muted">{loadError ? "Please try again in a moment." : units.length ? "Clear or adjust a filter to see more units." : "Check back when the developer releases unit pricing."}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {locked && visibleUnits.length > 0 && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4" style={{ top: gateTop }}>
              <div className="sticky top-24 mx-auto max-w-xl rounded-2xl border border-brand-100 bg-white/95 p-5 text-center shadow-[0_16px_50px_rgba(23,29,46,0.18)] backdrop-blur-sm md:p-6">
                <h3 className="font-display text-xl font-semibold tracking-tight text-ink">See every price for {propertyName}</h3>
                <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-ink-muted">Leave your name and mobile and every unit price and PSF on this page opens up. Winnie sends the same list to your WhatsApp.</p>
                <Button onClick={onUnlock} className="pointer-events-auto mt-4">Show every price</Button>
              </div>
            </div>
          )}
          </div>
        </div>
        {visibleCount < filteredUnits.length && (
          <div className="border-t border-line bg-white p-4 text-center">
            <Button variant="secondary" onClick={() => setVisibleCount((count) => count + INITIAL_ROWS)}>Show more</Button>
          </div>
        )}
      </div>

      {lightboxPlan !== null && (
        <FloorPlanLightbox plan={lightboxPlan} image={floorPlanImage(lightboxPlan)} propertyName={propertyName} onClose={() => setLightboxPlan(null)} />
      )}
    </section>
  );
}

function FilterInput({ label, value, placeholder, onChange }: { label: string; value: string; placeholder: string; onChange: (value: string) => void }) {
  return (
    <label className="block text-xs font-semibold text-ink-muted">
      {label}
      <input type="text" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className={`${inputClass} mt-1.5`} />
    </label>
  );
}

function FilterSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-ink-muted">
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value)} className={`${inputClass} mt-1.5 appearance-none`}>
        {children}
      </select>
    </label>
  );
}

function FloorPlanLightbox({ plan, image, propertyName, onClose }: { plan: string; image?: string; propertyName: string; onClose: () => void }) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="floor-plan-title" className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white p-4 shadow-2xl md:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 id="floor-plan-title" className="font-display text-xl font-semibold text-ink">Floor plan {plan || "not listed"}</h2>
            <p className="text-sm text-ink-muted">{propertyName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close floor plan" className="rounded-full p-2 text-ink-muted hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"><X aria-hidden="true" className="size-5" /></button>
        </div>
        <div className="relative mt-4 min-h-[55vh] flex-1 overflow-hidden rounded-xl bg-surface">
          {image ? <Image src={image} alt={`${plan} floor plan for ${propertyName}`} fill unoptimized sizes="(min-width: 1024px) 900px, 95vw" className="object-contain p-4" /> : <div className="flex min-h-[55vh] items-center justify-center px-6 text-center text-sm text-ink-muted">The developer has not released an image for this floor plan yet.</div>}
        </div>
      </div>
    </div>
  );
}
